import { t } from 'i18next';

import { AppDispatcher } from '../dispatcher/AppDispatcher';
import { ActionConstants } from '../constants/ActionConstants';
import { GlobalHelper } from '../utils/GlobalHelper';
import { ViewSettingsActions } from './ViewSettingsActions';
import { PropertyEditorActions } from './PropertyEditorActions';
import { OAPStore, OAPStoreState, OAPPositionSetting, OAPLevel } from '../stores/OAPStore';
import { ViewSettingsStore } from '../stores/ViewSettingsStore';
import { LayoutType } from '../constants/LayoutConstants';
import { PopupFlagPosition } from '../constants/PopupFlagPositions';

import core = egr.wcf.core;
import utils = egr.wcf.utils;

export class OAPActions {
    /**
     * Array of resolve-functions
     */
    protected static pendingOperations: Array<() => void> = [];
    protected static firstOperationTimeout: number | null = null;

    /**
     * Overrides wcf-core appCallback functions with OAPAction-functions
     * (showPropertyEditor, beginAsyncOperation and endAsyncOperation)
     */
    public static overrideAppCallbacks(): void {
        GlobalHelper.coreApp.appCallbacks.showActionChoice = OAPActions.showActionChoice;
        GlobalHelper.coreApp.appCallbacks.showPropertyEditor = OAPActions.showPropertyEditor;
        GlobalHelper.coreApp.appCallbacks.beginAsyncOperation = OAPActions.beginAsyncOperation;
        GlobalHelper.coreApp.appCallbacks.endAsyncOperation = OAPActions.endAsyncOperation;
    }

    public static setValue(property: core.prop.Property, value: core.prop.PropertyValueType): Promise<core.prop.PropertyChangedResult> {
        return PropertyEditorActions.setPropertyValue(property, value);
    }

    public static async showPropertyEditor(
        sceneElement: core.mdl.SceneElement,
        getPropertiesCallback: () => Promise<Array<core.prop.Property>>,
        pOptions: core.AppCallbacks.ShowPropertyEditorOptions
    ): Promise<void> {
        const createdStoreState: OAPStoreState = OAPStore.getInitialState();
        createdStoreState.title = pOptions.title;
        createdStoreState.position = OAPActions.getPositionSetting(
            ViewSettingsStore.getState().layoutType === LayoutType.SMALL ? null : pOptions.originReference
        );
        const properties: Array<core.prop.Property> = await getPropertiesCallback();

        createdStoreState.properties = properties.filter((value) => value.visible);
        createdStoreState.showEditor = true;

        let classes: Array<core.prop.PropertyClass> = [];

        const propertiesWithoutClass: Array<core.prop.Property> = properties.filter((property) => {
            return utils.string.isNullOrEmpty(property.class);
        });

        if (propertiesWithoutClass.length !== properties.length && sceneElement != null) {
            const propertyClasses: Array<core.prop.PropertyClass> | null = await sceneElement.getPropertyClasses();
            if (propertyClasses != null) {
                classes = propertyClasses;
            }
        }

        if (propertiesWithoutClass.length) {
            const newClass: core.prop.PropertyClass = new core.prop.PropertyClass();
            newClass.name = t('Miscellaneous');
            classes.push(newClass);
        }

        createdStoreState.classes = classes.filter((value) => {
            const propertiesOfClass: Array<core.prop.Property> = properties.filter((property) => {
                return property.class === value.key;
            });
            return propertiesOfClass.length > 0;
        });

        if (createdStoreState.classes.length === 1) {
            createdStoreState.selectedClass = createdStoreState.classes[0];
            createdStoreState.startLevel = OAPLevel.Properties;
        }

        if (createdStoreState.properties.length === 1) {
            const values: Array<core.prop.PropertyValue> | null = await createdStoreState.properties[0].getChoices();
            createdStoreState.selectedProperty = createdStoreState.properties[0];
            createdStoreState.values = values == null ? [] : values;
            createdStoreState.startLevel = OAPLevel.Values;
        }
        AppDispatcher.dispatch({
            actionType: ActionConstants.OAP_UPDATE,
            value: createdStoreState
        });
    }

    /**
     * Add an div to page and bind this function to onClick
     * it will show property editor as popup at this divs location
     *
     * !! Do not use in production its just for debugging !!
     */
    public static async toggleDialog(htmlElement: HTMLElement): Promise<void> {
        if (GlobalHelper.coreApp.model.selection[0] == null) {
            return;
        }
        OAPActions.showPropertyEditor(
            GlobalHelper.coreApp.model.selection[0],
            GlobalHelper.coreApp.model.selectionProperties.getProperties,
            { originReference: htmlElement.getBoundingClientRect() }
        );
    }

    /**
     * Returns name of Property
     * If value is null or name has substring '::' in it an fallback string is returned.
     * Fallback can be set by parameter, if none is set fallback will be the translated string 'Property'
     * @param value
     * @param fallback
     */
    public static getName(value: core.prop.Property, fallback: string = t('Property')): string {
        if (value == null) {
            return fallback;
        }
        const name: string = value.getName();
        if (name.indexOf('::') !== -1) {
            return fallback;
        }
        return name;
    }

    public static hideDialog(): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.OAP_DESTROY
        });
    }

    public static setSelectedClass(value: core.prop.PropertyClass | null): void {
        AppDispatcher.dispatch({
            actionType: ActionConstants.OAP_SET_CLASS,
            value: value
        });
    }

    public static async setSelectedProperty(value: core.prop.Property | null): Promise<void> {
        if (value == null) {
            AppDispatcher.dispatch({
                actionType: ActionConstants.OAP_SET_PROPERTY,
                value: {
                    values: new Array(),
                    property: null
                }
            });
        } else {
            const values: Array<core.prop.PropertyValue> | null = await value.getChoices();
            AppDispatcher.dispatch({
                actionType: ActionConstants.OAP_SET_PROPERTY,
                value: {
                    values: values,
                    property: value
                }
            });
        }
    }

    /**
     * Creates promise and adds resolve-function to a static array
     */
    protected static beginAsyncOperation(): void {
        if (OAPActions.pendingOperations.length === 0) {
            OAPActions.firstOperationTimeout = window.setTimeout(
                () => {
                    OAPActions.firstOperationTimeout = null;
                    ViewSettingsActions.withLock<void>(() => {
                        return new Promise<void>((resolve, reject) => {
                            OAPActions.pendingOperations.push(resolve);
                        });
                    });
                },
                200
            );
        } else {
            ViewSettingsActions.withLock<void>(() => {
                return new Promise<void>((resolve, reject) => {
                    OAPActions.pendingOperations.push(resolve);
                });
            });
        }
    }

    /**
     * Gets last resolve-function of a static array and runs it
     */
    protected static endAsyncOperation(): void {
        if (OAPActions.firstOperationTimeout != null) {
            clearTimeout(OAPActions.firstOperationTimeout);
            OAPActions.firstOperationTimeout = null;
        }
        if (OAPActions.pendingOperations.length > 0) {
            const lastEntry: (() => void) | undefined  = OAPActions.pendingOperations.pop();
            if (lastEntry != null) {
                lastEntry();
            }
        }
    }

    protected static async showActionChoice(
        actions: Array<core.AppCallbacks.ChoiceAction>,
        options: core.AppCallbacks.ShowActionChoiceOptions
    ): Promise<void> {
        const createdStoreState: OAPStoreState = OAPStore.getInitialState();
        createdStoreState.position = OAPActions.getPositionSetting(
            ViewSettingsStore.getState().layoutType === LayoutType.SMALL ? null :
            options.originReference
        );
        createdStoreState.actions = actions;
        createdStoreState.actionOptions = options;

        AppDispatcher.dispatch({
            actionType: ActionConstants.OAP_UPDATE,
            value: createdStoreState
        });

    }

    protected static getPositionSetting(clientRect: ClientRect | null | undefined): OAPPositionSetting {
        if (clientRect == null) {
            return {left: 0, top: 0, flagPosition: PopupFlagPosition.NONE};
        }
        const result: OAPPositionSetting = {
            left: Math.ceil(clientRect.left + clientRect.width / 2 - 1),
            top: Math.ceil(clientRect.top + clientRect.height / 2),
            flagPosition: PopupFlagPosition.NONE
        };
        const boundingRectBody: ClientRect = document.body.getBoundingClientRect();
        if (result.left > boundingRectBody.width / 2) {
            if (result.top > boundingRectBody.height / 2) {
                result.flagPosition = PopupFlagPosition.BOTTOMRIGHT;
            } else {
                result.flagPosition = PopupFlagPosition.TOPRIGHT;
            }
        } else if (result.top > boundingRectBody.height / 2) {
            result.flagPosition = PopupFlagPosition.BOTTOMLEFT;
        } else {
            result.flagPosition = PopupFlagPosition.TOPLEFT;
        }

        return result;
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/actions/OAPActions.ts